import { Temporal } from "@js-temporal/polyfill";
import api, { isRequestAborted } from "./api-utils.js";

/**
 * Alarm thresholds for trends.
 * @typedef {Object} RCAiTrendThresholdsDTO
 * @property {Number} lo
 * @property {Number} lolo
 * @property {Number} hi
 * @property {Number} hihi
 */

/**
 * Statistical characteristics of a trend.
 * @typedef {Object} RCAiTrendCharacteristicsDTO
 * @property {Number} win1Mean - the mean over the first window.
 * @property {Number} win2Mean - the mean over the second window.
 * @property {Number} driftPct - the percentage drift.
 */

/**
 * One per trend, containing some statistics about that trend prior to the shutdown.
 * @typedef {Object} RCAiTrendSummaryDTO
 * @property {Number} assetTagId - The asset tag id for the trend.
 * @property {string} universalDescriptor - The UD friendly name, if the trend has one.
 * @property {string} tagName - The actual tag name, only populated if the trend does not have a UD.
 * @property {string} uom - the unit of measure for the trend.
 * @property {Number} avg - the average value of the trend.
 * @property {Number} min - the minimum value of the trend.
 * @property {Number} max - the maximum value of the trend.
 * @property {RCAiTrendThresholdsDTO} thresholds - collection of alarm thresholds.
 * @property {RCAiTrendCharacteristicsDTO} chars - characteristics of the trend.
 */

/**
 * Root Cause Analysis AI request.
 * @typedef {Object} RCAiRequestDTO
 * @property {Number} downtimeEventId - The id of the downtime / shutdown event the user is running an AI-driven RCA for.
 * @property {string} DowntimeEventStartDate - ISO reporting date/time of the selected shutdown event.
 * @property {string|number} assetId - Fleet asset identifier for the shutdown.
 * @property {string} id - a GUID generated by the UI that can be used as a FK for storing adjacent data.
 * @property {Number} trendWindow - The period the user has selected for the trends in hours.
 * @property {Number} alarmAndDowntimeLimit - The limit the UI sets for how many Shutdown Events and Alarm History entries it shows.
 *                                            Used to match what the user sees to the data the LLM ingests (and avoid overwhelming the LLM.)
 * @property {string} [TimeZoneId] - Client IANA time zone; set by {@link runLLMStream} at send time.
 * @property {Number} win1mins - the first window's length in minutes.
 * @property {Number} win2mins - the second window's length in minutes.
 * @property {Array<RCAiTrendSummaryDTO>} summary - an array of trends with statistical data and other characteristics.
 */

/**
 * @typedef {Object} RCAiAnalyzeResponseDTO
 * @property {string} Id
 * @property {string} Words
 */

/**
 * @typedef {Object} RCAiResult
 * @property {string} id
 * @property {string} words
 */

/**
 * @typedef {Object} RCAiFeedbackRequestDTO
 * @property {string} id
 * @property {boolean} wasHelpful
 * @property {string} userRemarks
 * @property {string} feedbackChoices
 */

/**
 * @typedef {Object} RCAiStreamOptions
 * @property {(text: string) => void} [onDelta]
 * @property {(result: RCAiResult) => void} [onDone]
 * @property {AbortSignal} [signal]
 */

/**
 * @param {RCAiFeedbackRequestDTO} request
 */
export async function submitFeedback(request) {
  await api.post("/api/rca/feedback", {
    Id: request.id,
    WasHelpful: request.wasHelpful,
    UserRemarks: request.userRemarks,
    FeedbackChoices: request.feedbackChoices,
  });
}

/**
 * @param {RCAiAnalyzeRequestDTO} request
 * @returns {Promise<RCAiResult>}
 */
export async function runLLM(request) {
  const { data } = await api.post("/api/rca/analyze", {
    ...request,
    TimeZoneId: getClientTimeZoneId(),
  });

  return {
    id: data.Id,
    words: data.Words,
  };
}

/**
 * @returns {string} IANA time zone id for the current runtime (e.g. "America/Chicago").
 */
function getClientTimeZoneId() {
  return Temporal.Now.timeZoneId();
}

/**
 * Stream RCA analysis over SSE using a fetch adapter for axios since axois does not support SSE first class.
 *
 * @param {RCAiRequestDTO} request
 * @param {RCAiStreamOptions} [options]
 * @returns {Promise<RCAiResult>}
 */
export async function runLLMStream(request, { onDelta, onDone, signal } = {}) {
  const response = await api.post(
    "/api/rca/analyze/stream",
    {
      ...request,
      TimeZoneId: getClientTimeZoneId(),
    },
    {
      adapter: "fetch",
      responseType: "stream",
      headers: { Accept: "text/event-stream" },
      signal,
    }
  );

  if (response.status !== 200 || !response.data) {
    throw new Error("RCA stream request failed");
  }

  /** @type {RCAiResult|null} */
  let doneResult = null;

  const reader = response.data.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += value;
      const parsed = parseSseBuffer(buffer);
      buffer = parsed.remainder;

      for (const event of parsed.events) {
        handleSseEvent(event, {
          onDelta,
          onDone,
          setDoneResult: (result) => {
            doneResult = result;
          },
        });
      }
    }

    if (buffer.trim()) {
      const parsed = parseSseBuffer(`${buffer}\n\n`);
      for (const event of parsed.events) {
        handleSseEvent(event, {
          onDelta,
          onDone,
          setDoneResult: (result) => {
            doneResult = result;
          },
        });
      }
    }
  } finally {
    reader.releaseLock?.();
  }

  if (!doneResult) {
    throw new Error("RCA stream ended without a done event");
  }

  return doneResult;
}

/**
 * @param {{ event: string, data: string }} sseEvent
 * @param {{
 *   onDelta?: (text: string) => void,
 *   onDone?: (result: RCAiResult) => void,
 *   setDoneResult: (result: RCAiResult) => void,
 * }} handlers
 */
function handleSseEvent(sseEvent, { onDelta, onDone, setDoneResult }) {
  if (!sseEvent.data) {
    return;
  }

  const payload = JSON.parse(sseEvent.data);

  if (sseEvent.event === "delta") {
    const text = payload.Text ?? payload.text ?? "";
    if (text) {
      onDelta?.(text);
    }
    return;
  }

  if (sseEvent.event === "done") {
    const result = {
      id: payload.Id ?? payload.id,
      words: payload.Words ?? payload.words ?? "",
    };
    setDoneResult(result);
    onDone?.(result);
    return;
  }

  if (sseEvent.event === "error") {
    throw new Error(payload.Message ?? payload.message ?? "RCA stream failed");
  }
}

/**
 * @param {string} buffer
 * @returns {{ events: Array<{ event: string, data: string }>, remainder: string }}
 */
function parseSseBuffer(buffer) {
  /** @type {Array<{ event: string, data: string }>} */
  const events = [];
  let remainder = buffer.replace(/\r\n/g, "\n");
  let separatorIndex = remainder.indexOf("\n\n");

  while (separatorIndex >= 0) {
    const block = remainder.slice(0, separatorIndex);
    events.push(parseSseBlock(block));
    remainder = remainder.slice(separatorIndex + 2);
    separatorIndex = remainder.indexOf("\n\n");
  }

  return { events, remainder };
}

/**
 * @param {string} block
 * @returns {{ event: string, data: string }}
 */
function parseSseBlock(block) {
  let eventName = "message";
  /** @type {string[]} */
  const dataLines = [];

  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  return {
    event: eventName,
    data: dataLines.join("\n"),
  };
}

export { isRequestAborted };
